"use client";

import { useCallback, useEffect, useState } from "react";
import { useAuthoringScope } from "../authoring-scope";
import { PracticeToolIcon } from "../practice-tool-icon";
import { SkillDrillBrowser } from "../skills/drill-browser";
import { DrillBreadcrumbs } from "./breadcrumbs";
import { DrillIcon } from "./icon";
import { collectionHasCoOwners, collectionOwnerLabel } from "./owners";
import { DrillError, DrillShell, redirectForAuth } from "./shared";
import {
  type DrillCollection,
  dateLabel,
  RequestError,
  request,
} from "./types";

export function DrillCollectionLibrary({
  picking = false,
  layoutId,
}: {
  picking?: boolean;
  layoutId?: string;
}) {
  const authoring = useAuthoringScope();
  const [collections, setCollections] = useState<DrillCollection[]>();
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<"own" | "shared">("own");
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      setCollections(
        await request<DrillCollection[]>(
          authoring.api("/api/drill-collections"),
        ),
      );
    } catch (error) {
      if (redirectForAuth(error, authoring.href("/login"))) return;
      if (
        !authoring.admin &&
        error instanceof RequestError &&
        error.status === 404
      ) {
        window.location.assign("/account");
        return;
      }
      setError((error as Error).message);
    }
  }, [authoring]);
  useEffect(() => {
    void load();
  }, [load]);
  const createHref = authoring.href(
    `/drills/collections/new${layoutId ? `?layout=${encodeURIComponent(layoutId)}` : ""}`,
  );
  const scoped = collections?.filter((collection) =>
    picking
      ? collection.canEdit && !collection.isPlaylist
      : scope === "own"
        ? collection.canEdit
        : !collection.canEdit,
  );
  const shown = scoped?.filter((collection) =>
    `${collection.title} ${collection.description}`
      .toLocaleLowerCase("ja")
      .includes(query.toLocaleLowerCase("ja")),
  );
  function href(collection: DrillCollection) {
    return picking
      ? authoring.href(
          `/drills/new?collection=${collection.id}${layoutId ? `&layout=${encodeURIComponent(layoutId)}` : ""}`,
        )
      : authoring.href(`/drills/collections/${collection.id}`);
  }
  return (
    <DrillShell>
      <div className="drill-library-design drill-collections-view">
        <DrillBreadcrumbs
          current={picking ? "追加先のドリル集を選ぶ" : undefined}
        />
        <div className="library-design-heading">
          <div>
            <h1>{picking ? "追加先のドリル集を選ぶ" : "ドリル集"}</h1>
            <p className="library-design-description">
              {picking
                ? "ドリルを追加する集を選びましょう。"
                : "テーマごとに配置をまとめて、練習を積み重ねましょう。"}
            </p>
          </div>
          {collections && (scoped?.length || scope === "shared") ? (
            <a className="primary-button" href={createHref}>
              <DrillIcon name="plus" size={18} /> ドリル集を作成
            </a>
          ) : null}
        </div>
        {collections && !picking && !authoring.admin && (
          <fieldset
            className="drill-library-scopes"
            aria-label="ドリル集の種類"
          >
            {(
              [
                ["own", "自分のドリル集"],
                ["shared", "購入・公開されたドリル集"],
              ] as const
            ).map(([value, label]) => (
              <button
                type="button"
                key={value}
                aria-pressed={scope === value}
                onClick={() => {
                  setScope(value);
                  setQuery("");
                }}
              >
                {label}{" "}
                <span>
                  {
                    collections.filter((collection) =>
                      value === "own"
                        ? collection.canEdit
                        : !collection.canEdit,
                    ).length
                  }
                </span>
              </button>
            ))}
          </fieldset>
        )}
        {!picking && !authoring.admin && (
          <p>
            <a className="text-link" href="/shop#drill-store-title">
              ストアで練習教材を探す <span aria-hidden="true">→</span>
            </a>
          </p>
        )}
        {!picking && !authoring.admin && <SkillDrillBrowser />}
        {error && (
          <>
            <DrillError message={error} />
            <button
              type="button"
              className="secondary-button"
              onClick={() => void load()}
            >
              再読み込み
            </button>
          </>
        )}
        {!collections && !error && (
          <p className="drill-loading" role="status">
            ドリル集を読み込み中…
          </p>
        )}
        {scoped?.length === 0 && (
          <section className="library-design-empty">
            <div className="drill-empty-symbol">
              <PracticeToolIcon name="drills" size={40} />
            </div>
            <h2>
              {!picking && scope === "shared"
                ? "購入・公開されたドリル集はまだありません"
                : "最初のドリル集を作りましょう"}
            </h2>
            <p>
              {!picking && scope === "shared"
                ? "購入した教材や、管理者からあなたに公開されたドリル集が表示されます。"
                : "「基本ショット」「ポジショニング」など、練習のテーマを決めて配置を集められます。"}
            </p>
            {(picking || scope === "own") && (
              <>
                <div className="drill-empty-steps">
                  <span>
                    <b>01</b> 集を作る
                  </span>
                  <span>
                    <b>02</b> ドリルを追加
                  </span>
                  <span>
                    <b>03</b> 一球ずつ練習
                  </span>
                </div>
                <a className="primary-button" href={createHref}>
                  最初のドリル集を作成 <DrillIcon name="arrow" size={18} />
                </a>
              </>
            )}
          </section>
        )}
        {scoped && scoped.length > 0 && (
          <>
            <div className="library-design-toolbar">
              <p className="library-design-count">
                ドリル集 <strong>{scoped.length}</strong>
              </p>
              <label className="drill-search">
                <DrillIcon name="search" size={17} />
                <input
                  type="search"
                  aria-label="ドリル集を検索"
                  placeholder="ドリル集を検索"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
            </div>
            <p className="drill-sr-only" role="status">
              {shown?.length}件のドリル集
            </p>
            <div className="library-design-collection-grid content-grid content-grid-cards">
              {shown?.map((collection) => (
                <article
                  className="library-design-collection"
                  key={collection.id}
                >
                  <div className="library-design-collection-top">
                    <span className="library-design-collection-symbol">
                      <PracticeToolIcon name="drills" size={22} />
                    </span>
                    <span className="library-design-drill-count">
                      <b>{collection.drillCount}</b> ドリル
                    </span>
                  </div>
                  <h2>
                    <a href={href(collection)}>
                      {collection.title}
                      <DrillIcon name="arrow" size={18} />
                    </a>
                  </h2>
                  {(!collection.canEdit ||
                    collectionHasCoOwners(collection)) && (
                    <p className="drill-shared-provider">
                      提供者: {collectionOwnerLabel(collection)}
                    </p>
                  )}
                  <p className="library-design-card-description">
                    {collection.description ||
                      "練習する配置を、このドリル集に。"}
                  </p>
                  {collection.isPlaylist ? (
                    <p className="skill-picker-count">
                      選択したドリル集 · 元ドリルの練習設定を使用
                    </p>
                  ) : (
                    <p className="skill-picker-count">
                      練習方法は自分で選べます
                    </p>
                  )}
                  <div className="library-design-card-footer">
                    <span>更新 {dateLabel(collection.updatedAt)}</span>
                    {picking && (
                      <a className="text-link" href={href(collection)}>
                        この集に追加 <DrillIcon name="plus" size={15} />
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
            {shown?.length === 0 && (
              <div className="library-design-empty library-design-filter-empty">
                <h2>一致するドリル集がありません</h2>
                <p>名前や説明を変えて検索してください。</p>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setQuery("")}
                >
                  すべてのドリル集を表示
                </button>
              </div>
            )}
            <p className="drill-footnote">
              最新100件のドリル集を表示しています。
            </p>
          </>
        )}
      </div>
    </DrillShell>
  );
}
